import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, currentAttempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (currentAttempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/checkout/status/${sessionId}`
      );

      const data = response.data;
      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling if still pending
      setTimeout(() => {
        setAttempts(currentAttempt + 1);
        pollPaymentStatus(sessionId, currentAttempt + 1);
      }, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center py-12">
      {/* Patriotic background */}
      <div 
        className="fixed inset-0 opacity-5"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1563804846955-f87a66f0b20a?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      ></div>
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-blue-50/40 to-red-50/40"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          {status === 'checking' && (
            <Card className="text-center bg-white/90 backdrop-blur-sm">
              <CardContent className="p-12">
                <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Verifying Payment...</h2>
                <p className="text-slate-600 mb-2">Please wait while we confirm your payment.</p>
                <p className="text-sm text-slate-500">Attempt {attempts + 1} of 5</p>
              </CardContent>
            </Card>
          )}

          {status === 'success' && (
            <Card className="text-center bg-white/90 backdrop-blur-sm border-4 border-green-500">
              <CardContent className="p-12">
                <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
                <h2 className="text-4xl font-bold text-slate-900 mb-4">Payment Successful!</h2>
                <p className="text-xl text-slate-600 mb-6">
                  Thank you for your order! Your payment has been processed successfully.
                </p>
                
                {paymentDetails && (
                  <div className="bg-slate-50 rounded-lg p-6 mb-6 text-left">
                    <h3 className="font-semibold text-lg mb-4">Order Details</h3>
                    <div className="space-y-2 text-slate-700">
                      <div className="flex justify-between">
                        <span>Amount Paid:</span>
                        <span className="font-bold">${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Payment Status:</span>
                        <span className="font-bold text-green-600">Paid</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Session ID:</span>
                        <span className="font-mono text-sm">{sessionId?.substring(0, 20)}...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <p className="text-slate-600">
                    We've sent a confirmation email to your registered email address. 
                    Our team will contact you shortly to arrange delivery and installation.
                  </p>
                  
                  {/* Google Review Request */}
                  <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6 my-6">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <span className="text-yellow-500 text-2xl">⭐⭐⭐⭐⭐</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2 text-center">Enjoying Your Experience?</h3>
                    <p className="text-slate-600 text-center mb-4">
                      We'd love to hear from you! Leave us a Google review and help other coastal customers find us.
                    </p>
                    <div className="text-center">
                      <a 
                        href="https://www.google.com/search?q=Coastal+Flag+Company+Gulf+Coast&hl=en#lrd="
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-8 py-3 rounded-full transition-colors"
                      >
                        Leave a Google Review
                      </a>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      onClick={() => navigate('/')}
                      className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                      size="lg"
                    >
                      Return to Home
                    </Button>
                    <Button 
                      onClick={() => navigate('/us-flags')}
                      variant="outline"
                      size="lg"
                      className="border-2"
                    >
                      Continue Shopping
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {(status === 'failed' || status === 'error' || status === 'timeout') && (
            <Card className="text-center bg-white/90 backdrop-blur-sm border-4 border-red-500">
              <CardContent className="p-12">
                <XCircle className="w-20 h-20 text-red-600 mx-auto mb-4" />
                <h2 className="text-4xl font-bold text-slate-900 mb-4">Payment Issue</h2>
                <p className="text-xl text-slate-600 mb-6">
                  {status === 'timeout' 
                    ? 'Payment verification timed out. Please check your email for confirmation or contact us.'
                    : 'There was an issue processing your payment. Please try again or contact support.'}
                </p>
                
                <div className="bg-slate-50 rounded-lg p-6 mb-6">
                  <p className="text-slate-700 mb-2">Need help?</p>
                  <p className="font-semibold text-lg">Call: 205-587-2402</p>
                  <p className="text-slate-600">Email: coastalflagco@yahoo.com</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={() => navigate('/checkout')}
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                    size="lg"
                  >
                    Try Again
                  </Button>
                  <Button 
                    onClick={() => navigate('/')}
                    variant="outline"
                    size="lg"
                    className="border-2"
                  >
                    Return to Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;